import {
  GRID_AGGREGATION_FUNCTIONS,
  GridColDef,
  GridEventListener,
  GridRowId,
  GridRowParams,
  GridToolbar,
  GridValidRowModel,
} from '@mui/x-data-grid-premium'
import {
  EditProductionMUITableProps,
  PanelDataType,
  PanelSiteDataType,
  SkuDataType,
} from './EditProduction.types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { Box, CircularProgress } from '@mui/material'
import { Loader } from 'Common/Loader/Loader'
import { autoHeight } from 'Common/utils/utils'
import { PanelContentTable } from './PanelContentTable.MUITable'
import { usePanelData } from './EditProduction.data'
import { Button } from 'Common/Supplyvue-ui/Button'
import toast from 'react-hot-toast'
import {
  useRunUpdateVolume,
  useUpdateVolume,
} from 'Features/PlannedVolume/PlannedVolume.data'
import { useNavigate } from 'react-router-dom'
import { getApiErrorMessage } from 'Features/auth/axios-client'
import { usePlanBuiltByV2Engine } from 'Common/Charts/StockWaterfall/stockWaterfall.data'

import {
  innerTableReduce,
  outerTableReduce,
  roundNumber,
} from './helperMethods'
import { renderCellAggregation, renderCellNoAggregation } from './Aggragation'
import { CustomColumnsPanel } from './CustomToolbar'
import {
  aggragationModelObj,
  columnGroupingModel,
  columnVisibilityModel,
  editProductionColumns,
} from './Column.definintion'
import { UpdateVolumeParams } from 'Features/PlannedVolume/PlannedVolume.types'

export const EditProductionMUITable = ({
  data,
  isLoading,
  weeks,
  planId,
  workcentreId,
  sourcesiteId,
  isAnyWorkcentreRunning,
  setIsSavePending,
}: EditProductionMUITableProps) => {
  const navigate = useNavigate()
  const gridApiRef = useRef<any>(null) // Store apiRef from BaseTable
  const [editedRowId, setEditedRowId] = useState<string | null>(null)
  const [latestRow, setLatestRow] = useState<GridValidRowModel | null>(null)
  const [editedWeek, setEditedWeek] = useState<string | null>(null)
  const updatedDataRef = useRef<UpdateVolumeParams[]>([])
  const [expandedPanelId, setExpandedPanelId] = useState<GridRowId[]>([])
  const panelDataBySkuRef = useRef<PanelDataType>({})
  const [panelData, setPanelData] = useState<PanelSiteDataType[]>([])

  const onSuccess = (savePlan: 'true' | 'false') => {
    runPlan({ planId, sourcesiteId, workcentreId, savePlan })
    toast.success('Volume updated successfully.')
  }

  const onError = () => {
    toast.error('Could not update the value. Please retry.')
  }
  const { mutate: updateVolume, isLoading: isPlanUpdating } = useUpdateVolume(
    onSuccess,
    onError
  )

  const onRunPlanSuccess = () => {
    toast.success('Plan initiated successfully.')
    navigate(`/plan/${planId}`)
  }

  const onRunPlanError = (error: unknown) => {
    toast.error(
      getApiErrorMessage(error, 'Could not update the value. Please retry.')
    )
  }

  const { mutate: runPlan, isLoading: isPlanInitializationRunning } =
    useRunUpdateVolume(onRunPlanSuccess, onRunPlanError)

  // Save plan / Apply changes write the volume, then run the plan; the run is
  // refused on V2 plans, so both stay disabled there (no half-applied edit).
  const { builtByV2Engine } = usePlanBuiltByV2Engine(planId)

  const updateProposedProductionAmountFromInnerTable = async (
    newValue: number,
    rowId: string | number,
    week: string,
    editedInnerTableRow: PanelSiteDataType
  ) => {
    const row = gridApiRef.current?.getRow(rowId)
    if (!row) return
    row[week].proposedProductionAmount = newValue
    //
    const proposedProductionAmount =
      parseFloat(row[week].proposedProductionAmount) ?? 0
    const runRate = parseFloat(row.runRate) ?? 0
    const initialProductionAmount =
      parseFloat(row[week].initialProductionAmount) ?? 0
    const alternativeWCProductionAmount = row[week]
      .alternativeWCProductionAmount
      ? parseFloat(row[week].alternativeWCProductionAmount)
      : 0

    row[week].adjustedProductionHours = roundNumber(
      proposedProductionAmount / runRate
    )
    row[week].changeInProductionAmount = roundNumber(
      proposedProductionAmount - initialProductionAmount
    )
    row[week].totalProductionAmount = roundNumber(
      proposedProductionAmount + alternativeWCProductionAmount
    )
    //
    const newRow = outerTableReduce(row, weeks, newValue, runRate)
    gridApiRef.current?.updateRows([newRow])
    panelDataBySkuRef.current[row.skuCode][editedInnerTableRow.siteCode] =
      editedInnerTableRow
    updateServerDataForInnerTableChange(row.skuCode, week, row.skuKey)
  }
  /*
  updateServerDataForInnerTableChange and updateServerData are doing the same thing but calculations are different.
  The latter is for the outer table table change to propagate inside and the values are calculated according to the set percentage.
  The former is for the inner table change to propagate outside and the values are taken as it is and no calculation is involved.
  */
  const updateServerDataForInnerTableChange = (
    skuCode: string,
    week: string,
    skuKey: number
  ) => {
    const siteData = panelDataBySkuRef.current[skuCode]
    const rows = Object.values(siteData)
    rows.forEach((row) => {
      if (row[week]) {
        const proposedArrivalAmount = row[week]?.proposedArrivalAmount

        const index = updatedDataRef.current?.findIndex((item) => {
          return (
            item.skuKey === skuKey &&
            item.productionDateKey === row[week].productionDateKey &&
            item.siteKey === row.siteKey
          )
        })
        const newValueObj = {
          planId: planId,
          productionDateKey: row[week].productionDateKey,
          replenishmentDateKey: row[week].replenishmentDateKey,
          workcentreId: workcentreId,
          sourcesiteId: sourcesiteId,
          skuKey: skuKey,
          siteKey: row?.siteKey,
          replenishmentSiteKey: row.siteKey,
          adjustedPlannedVolume: proposedArrivalAmount,
        }
        if (index !== -1) {
          updatedDataRef.current[index] = newValueObj
        } else {
          updatedDataRef.current.push(newValueObj)
        }
      }
    })
  }

  // Now we are bringing all the sku data at once and keep it in the Ref.
  const { data: panelDataBySku, isLoading: isPanelDataBySkuLoading } =
    usePanelData({
      planId,
      workcentreId,
    })

  useEffect(() => {
    if (panelDataBySku) {
      panelDataBySkuRef.current = panelDataBySku
    }
  }, [panelDataBySku])

  const updateServerData = (
    skuCode: string,
    week: string,
    changeInProductionAmount: number,
    skuKey: number
  ) => {
    const siteData = panelDataBySkuRef.current[skuCode]
    const rows = Object.values(siteData)
    rows.forEach((row) => {
      if (row[week]) {
        const volumePercentageExcess = parseFloat(
          (row[week]?.volumePercentageExcess as string) ?? 0
        )
        const initialArrivalAmount = parseFloat(
          row[week].initialArrivalAmount as string
        )
        const proposedArrivalAmount =
          initialArrivalAmount +
          changeInProductionAmount * volumePercentageExcess
        const index = updatedDataRef.current?.findIndex((item) => {
          return (
            item.skuKey === skuKey &&
            item.productionDateKey === row[week].productionDateKey &&
            item.siteKey === row.siteKey
          )
        })
        const newValueObj = {
          planId: planId,
          productionDateKey: row[week].productionDateKey,
          replenishmentDateKey: row[week].replenishmentDateKey,
          workcentreId: workcentreId,
          sourcesiteId: sourcesiteId,
          skuKey: skuKey,
          siteKey: row?.siteKey,
          replenishmentSiteKey: row.siteKey,
          adjustedPlannedVolume: proposedArrivalAmount,
        }
        if (index !== -1) {
          updatedDataRef.current[index] = newValueObj
        } else {
          updatedDataRef.current.push(newValueObj)
        }
      }
    })
  }

  const updateInnerTableData = (
    skuCode: string,
    week: string,
    change: number,
    proposedProductionAmount: number
  ) => {
    const siteData = panelDataBySkuRef.current[skuCode] as Record<string, any>
    const rows = Object.values(siteData)
    let anyProposedArrivalAmountLessThanZero = false
    rows.forEach((row) => {
      if (row[week]) {
        const volumePercentageExcess = parseFloat(
          row[week]?.volumePercentageExcess ?? 0
        )
        const initialArrivalAmount = parseFloat(row[week].initialArrivalAmount)
        let proposedArrivalAmount = roundNumber(
          initialArrivalAmount + change * volumePercentageExcess
        )
        if (proposedArrivalAmount < 0) {
          anyProposedArrivalAmountLessThanZero = true
        }
        panelDataBySkuRef.current[skuCode][row.siteCode][
          week
        ].proposedArrivalAmount = proposedArrivalAmount
        panelDataBySkuRef.current[skuCode][row.siteCode][
          week
        ].changeInArrivalAmount = proposedArrivalAmount - initialArrivalAmount
        panelDataBySkuRef.current[skuCode][row.siteCode] = innerTableReduce(
          panelDataBySkuRef.current[skuCode][row.siteCode],
          weeks
        )
      }
    })

    if (anyProposedArrivalAmountLessThanZero) {
      const initialProductionAmount = proposedProductionAmount - change
      const percentageOfProduction =
        proposedProductionAmount / initialProductionAmount
      rows.forEach((row) => {
        if (row[week]) {
          row[week].proposedArrivalAmount = roundNumber(
            row[week].initialArrivalAmount * percentageOfProduction
          )
          row[week].changeInArrivalAmount =
            row[week].proposedArrivalAmount - row[week].initialArrivalAmount
          panelDataBySkuRef.current[skuCode][row.siteCode][week] = {
            ...row[week],
          }
        }
      })
    }

    if (expandedPanelId.length > 0) {
      const data = Object.values(panelDataBySkuRef.current[skuCode])
      setPanelData(data)
    }
  }

  useEffect(() => {
    if (expandedPanelId.length > 0) {
      const panelId = expandedPanelId[0]
      //get skucode from table with panelId.
      const row = gridApiRef.current?.getRow(panelId)
      const skuCode = row?.skuCode as string
      const data = Object.values(panelDataBySkuRef.current[skuCode])
      setPanelData(data)
    }
  }, [expandedPanelId])

  const processRowUpdate = (newRow: GridValidRowModel) => {
    //_proposedProductionAmount or _alternativeWCProductionAmount is added to the week key.
    const week: string = weeks.find(
      (week) =>
        newRow[`${week}_proposedProductionAmount`] !== undefined ||
        newRow[`${week}_adjustedProductionHours`] !== undefined
    ) as string

    if (newRow[`${week}_adjustedProductionHours`] !== undefined) {
      newRow[`${week}_proposedProductionAmount`] =
        newRow[`${week}_adjustedProductionHours`] * parseFloat(newRow.runRate)
      delete newRow[`${week}_adjustedProductionHours`]
    }

    if (newRow[week]) {
      newRow[week].proposedProductionAmount = roundNumber(
        Math.abs(newRow[`${week}_proposedProductionAmount`])
      )
      delete newRow[`${week}_proposedProductionAmount`]
      const proposedProductionAmount =
        parseFloat(newRow[week].proposedProductionAmount) ?? 0
      const runRate = parseFloat(newRow.runRate) ?? 0
      const initialProductionAmount =
        parseFloat(newRow[week].initialProductionAmount) ?? 0
      const alternativeWCProductionAmount = newRow[week]
        .alternativeWCProductionAmount
        ? parseFloat(newRow[week].alternativeWCProductionAmount)
        : 0
      newRow[week].adjustedProductionHours = roundNumber(
        proposedProductionAmount / runRate
      )
      newRow[week].changeInProductionAmount = roundNumber(
        proposedProductionAmount - initialProductionAmount
      )
      newRow[week].totalProductionAmount = roundNumber(
        proposedProductionAmount + alternativeWCProductionAmount
      )

      newRow = outerTableReduce(
        newRow as SkuDataType,
        weeks,
        proposedProductionAmount,
        runRate
      )

      const skuCode = newRow.skuCode
      updateServerData(
        skuCode,
        week,
        newRow[week].changeInProductionAmount,
        newRow.skuKey
      )
      setEditedRowId(newRow.id)
      setLatestRow(newRow)
      setEditedWeek(week)
      updateInnerTableData(
        skuCode,
        week,
        newRow[week].changeInProductionAmount,
        proposedProductionAmount
      )
    }
    delete newRow[`${week}_proposedProductionAmount`]
    return { ...newRow }
  }

  // Generate columns for the dummy data except for id key.
  const columns = useMemo(() => {
    return editProductionColumns(weeks, data)
  }, [data])

  const dynamicColumnGroupingModel = useMemo(() => {
    return columnGroupingModel(weeks)
  }, [weeks])

  const hiddenColumns = useMemo(() => {
    return columnVisibilityModel(weeks, columns)
  }, [weeks])

  const aggregationModel = useMemo(() => {
    return aggragationModelObj(weeks)
  }, [weeks])

  const handleSavePlanButtonClick = () => {
    const data = {
      dataToServer: updatedDataRef.current,
      savePlan: 'true' as 'true' | 'false',
    }
    updateVolume(data)
  }

  const handleApplyChangesButtonClick = () => {
    const data = {
      dataToServer: updatedDataRef.current,
      savePlan: 'false' as 'true' | 'false',
    }
    updateVolume(data)
  }

  const isApplyChangesButtonDisabled = () => {
    if (
      isAnyWorkcentreRunning ||
      isPlanUpdating ||
      isPlanInitializationRunning ||
      latestRow === null ||
      builtByV2Engine
    ) {
      return true
    }
    return false
  }

  const handleDetailPanelExpandedRowIdsChange = useCallback(
    (newIds: GridRowId[]) => {
      setPanelData([])
      setExpandedPanelId(
        newIds.length > 1 ? [newIds[newIds.length - 1]] : newIds
      )
    },
    []
  )

  const panelContent = useCallback(
    ({ row }: GridRowParams<GridValidRowModel>) => {
      return (
        <PanelContentTable
          weeks={weeks}
          data={panelData}
          row={row as SkuDataType}
          updateProposedProductionAmountFromInnerTable={
            updateProposedProductionAmountFromInnerTable
          }
        />
      )
    },
    [panelData, weeks, planId, workcentreId, editedRowId, latestRow, editedWeek]
  )

  const onCellClick = useCallback<GridEventListener<'cellClick'>>(
    (params) => {
      if (params.field === 'skuCode') {
        gridApiRef.current?.toggleDetailPanel(params.id)
      }
    },
    [gridApiRef]
  )

  const columnPanel = () => {
    return <CustomColumnsPanel weeks={weeks} />
  }

  if (isLoading || isPanelDataBySkuLoading) {
    return <Loader />
  }

  return (
    <Box>
      <Box
        id="#table-container-mui"
        sx={{
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          // light green color
          '& .calculated': {
            // backgroundColor: '#D1FAE5',
          },
          // light orange color
          '& .cumulative-calculated': {
            backgroundColor: '#FEEBC8',
          },
          scrollbarWidth: 'thin',
          overflowX: 'auto',
          maxWidth: '97vw',
        }}
      >
        <BaseTable
          data={data}
          columns={columns as ExtendedGridColDef[]}
          isLoading={isLoading}
          updateTableData={() => {}} // Handled by processRow
          processRow={processRowUpdate}
          uploadData={() => {}} // Not used
          isUploading={false}
          isSaving={false}
          checkBoxSelection={false}
          showTableToolBar={true}
          onCellClick={onCellClick}
          columnGroupingModel={dynamicColumnGroupingModel}
          pinnedColumns={['skuCode']}
          initialState={{
            columns: {
              columnVisibilityModel: hiddenColumns,
            },
            aggregation: {
              model: aggregationModel,
            },
            pinnedColumns: {
              left: ['skuCode'],
            },
          }}
          // Master-Detail Panel props
          getDetailPanelContent={panelContent}
          getDetailPanelHeight={() => 'auto'}
          detailPanelExpandedRowIds={expandedPanelId}
          onDetailPanelExpandedRowIdsChange={
            handleDetailPanelExpandedRowIdsChange
          }
          // Aggregation props
          aggregationFunctions={{
            ...GRID_AGGREGATION_FUNCTIONS,
            renderCellAggregation: renderCellAggregation,
            renderCellNoAggregation: renderCellNoAggregation,
          }}
          customToolbar={() => () => <GridToolbar />}
          apiRefCallback={(apiRef) => {
            gridApiRef.current = apiRef.current
          }}
          downloadFileName="EditProduction"
          enableBlocker={false}
        />
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          margin: '16px 0px',
        }}
      >
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSavePlanButtonClick}
            disabled={isApplyChangesButtonDisabled()}
          >
            {isPlanInitializationRunning && (
              <CircularProgress
                color="inherit"
                size={16}
                sx={{ marginRight: '8px' }}
              />
            )}
            Save plan
          </Button>
        </Box>

        <Box marginLeft={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyChangesButtonClick}
            disabled={isApplyChangesButtonDisabled()}
          >
            {isPlanUpdating && (
              <CircularProgress
                color="inherit"
                size={16}
                sx={{ marginRight: '8px' }}
              />
            )}
            Apply changes
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
